import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Coins, Square, RefreshCw, Sparkles } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "needs_render", label: "Needs render" },
  { key: "awaiting_approval", label: "Awaiting approval" },
  { key: "approved", label: "Approved" },
  { key: "changed", label: "Changed" },
];

function rowMatches(r, f) {
  if (f === "all") return true;
  if (f === "needs_render") return r.needs_render;
  if (f === "changed") return r.stale;
  if (f === "approved") return r.take_approved && !r.stale;
  if (f === "awaiting_approval") return r.has_take && !r.take_approved && !r.stale;
  return true;
}

function StateBadge({ r }) {
  if (r.stale) return <span className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-red-500/15 text-red-300 ring-1 ring-red-500/30">changed</span>;
  if (r.take_approved) return <span className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30">approved</span>;
  if (r.has_take) return <span className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30">take · unheard</span>;
  return <span className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-slate-600/25 text-slate-300 ring-1 ring-slate-500/30">no take</span>;
}

export function BulkReviewTable({ projectId, chapterId, onChanged }) {
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/production/chapters/${chapterId}/bulk`);
    setData(data);
    setJob(data.active_job || null);
    return data;
  }, [projectId, chapterId]);

  useEffect(() => { setChecked(new Set()); setFilter("all"); load(); }, [load]);

  // poll a running job
  useEffect(() => {
    if (!job || job.status !== "running") { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } return; }
    pollRef.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/projects/${projectId}/production/render-jobs/${job.id}`);
        setJob(j);
        if (j.status !== "running") {
          clearInterval(pollRef.current); pollRef.current = null;
          toast[j.failed ? "warning" : "success"](`Render ${j.status}: ${j.done}/${j.total} done${j.failed ? `, ${j.failed} failed` : ""}.`);
          await load(); if (onChanged) onChanged();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [job, projectId, load, onChanged]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>;

  const rows = data.rows.filter((r) => rowMatches(r, filter));
  const t = data.totals;
  const allChecked = rows.length > 0 && rows.every((r) => checked.has(r.id));
  const toggleAll = () => setChecked((s) => {
    const n = new Set(s);
    if (allChecked) rows.forEach((r) => n.delete(r.id));
    else rows.forEach((r) => n.add(r.id));
    return n;
  });
  const toggle = (id) => setChecked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const checkedIds = [...checked];
  const selRows = data.rows.filter((r) => checked.has(r.id));
  const selApprovable = selRows.filter((r) => r.has_take && !r.stale).length;
  const selNeedsRender = selRows.filter((r) => r.needs_render).length;

  const approveChecked = async () => {
    if (!checkedIds.length) { toast.message("Select at least one passage first."); return; }
    const selRows = data.rows.filter((r) => checked.has(r.id));
    const approvable = selRows.filter((r) => r.has_take && !r.stale);
    const noTake = selRows.filter((r) => !r.has_take);
    const staleSel = selRows.filter((r) => r.has_take && r.stale);
    if (approvable.length === 0) {
      toast.error(
        noTake.length && !staleSel.length
          ? `None of the ${selRows.length} selected passages have a take yet — use "Paid render checked" to generate them first, then approve.`
          : staleSel.length && !noTake.length
          ? `All ${selRows.length} selected takes are stale (the script changed) — re-render them first, then approve.`
          : `Nothing to approve — ${noTake.length} have no take and ${staleSel.length} are stale. Render / re-render those first.`,
        { duration: 6000 });
      return;
    }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/production/takes/approve-bulk`, { passage_ids: checkedIds });
      toast.success(`Approved ${r.approved} take(s).${r.skipped ? ` Skipped ${r.skipped} (no take yet or stale — render those first).` : ""}`);
      setChecked(new Set());
      await load(); if (onChanged) onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openRenderPreview = async () => {
    if (!checkedIds.length) return;
    setBusy(true);
    try {
      const { data: p } = await api.post(`/projects/${projectId}/production/chapters/${chapterId}/render-bulk`, { passage_ids: checkedIds, confirm: false });
      setPreflight(p);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startRender = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/production/chapters/${chapterId}/render-bulk`, { passage_ids: checkedIds, confirm: true });
      setPreflight(null);
      setChecked(new Set());
      toast.success(`Render started — ${r.eligible} passage(s), ~${r.estimated_credits} credits.`);
      const { data: j } = await api.get(`/projects/${projectId}/production/render-jobs/${r.job_id}`);
      setJob(j);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const stopJob = async () => {
    try { await api.post(`/projects/${projectId}/production/render-jobs/${job.id}/stop`); toast.message("Stopping after the current passage…"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const running = job && job.status === "running";

  return (
    <div className="mt-4" data-testid="bulk-review-table">
      {/* filters + totals */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2" data-testid="bulk-filters">
          {FILTERS.map((f) => {
            const n = f.key === "all" ? t.total : t[f.key];
            return (
              <button key={f.key} data-testid={`bulk-filter-${f.key}`} onClick={() => setFilter(f.key)}
                className={`rounded-md border px-2.5 py-1 text-xs transition-all ${filter === f.key ? "border-amber-500/50 bg-amber-500/15 text-amber-200" : "border-border bg-[#0d1016] text-slate-400 hover:text-slate-200"}`}>
                {f.label} <span className="ml-1 font-mono text-[10px] text-slate-500">{n}</span>
              </button>
            );
          })}
        </div>
        <button onClick={() => load()} data-testid="bulk-refresh" className="flex items-center gap-1 text-xs text-slate-500 hover:text-amber-300"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>

      {/* running job progress */}
      {running && (
        <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3" data-testid="bulk-job-progress">
          <div className="mb-2 flex items-center justify-between text-xs text-amber-200">
            <span className="flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering {job.current_label} · {job.done + job.failed}/{job.total} · {job.actual_credits} credits used</span>
            <Button size="sm" variant="outline" data-testid="bulk-stop" onClick={stopJob} className="h-7 gap-1 border-red-500/40 text-red-300 text-xs hover:bg-red-500/10"><Square className="h-3 w-3" /> Stop</Button>
          </div>
          <Progress value={Math.round(((job.done + job.failed) / Math.max(1, job.total)) * 100)} className="h-2" />
          {job.failed > 0 && <div className="mt-1 text-[11px] text-red-300">{job.failed} failed — see details after completion.</div>}
        </div>
      )}

      {/* table */}
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#0d1016] text-slate-400">
            <tr>
              <th className="w-10 p-2"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="bulk-select-all" aria-label="Select all" /></th>
              <th className="p-2 font-mono text-[10px] uppercase tracking-wider">Passage</th>
              <th className="p-2 font-mono text-[10px] uppercase tracking-wider">Text</th>
              <th className="p-2 font-mono text-[10px] uppercase tracking-wider">State</th>
              <th className="p-2 text-right font-mono text-[10px] uppercase tracking-wider">Chars</th>
              <th className="p-2 text-right font-mono text-[10px] uppercase tracking-wider">Est. credits</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-sm text-slate-500">No passages match this filter.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`bulk-row-${r.index}`} className={`border-t border-border/60 ${checked.has(r.id) ? "bg-amber-500/[0.04]" : "hover:bg-slate-800/20"}`}>
                <td className="p-2 align-top"><Checkbox checked={checked.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`bulk-check-${r.index}`} aria-label={`Select ${r.label}`} /></td>
                <td className="p-2 align-top font-mono text-[11px] text-slate-400 whitespace-nowrap">
                  {r.label}
                  {r.has_narration_override && <span className="ml-1 rounded bg-amber-500/15 px-1 text-[9px] text-amber-300">ovr</span>}
                  {r.emphasis_marks > 0 && (
                    <span data-testid={`bulk-emphasis-${r.index}`}
                      title={`${r.emphasis_confirmed} of ${r.emphasis_marks} emphasis mark${r.emphasis_marks === 1 ? "" : "s"} confirmed`}
                      className={`ml-1 inline-flex items-center gap-0.5 rounded px-1 text-[9px] ${r.emphasis_confirmed >= r.emphasis_marks ? "bg-fuchsia-500/20 text-fuchsia-200" : "bg-fuchsia-500/10 text-fuchsia-300/80"}`}>
                      <Sparkles className="h-2.5 w-2.5" />{r.emphasis_confirmed}/{r.emphasis_marks}
                    </span>
                  )}
                </td>
                <td className="p-2 align-top text-[13px] text-slate-300"><span className="line-clamp-2">{r.text_preview}</span></td>
                <td className="p-2 align-top"><StateBadge r={r} /></td>
                <td className="p-2 align-top text-right font-mono text-xs text-slate-400">{r.char_count.toLocaleString()}</td>
                <td className="p-2 align-top text-right font-mono text-xs text-slate-400">{r.needs_render ? r.estimated_credits.toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* action bar */}
      <div className="sticky bottom-4 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-[#12161f]/95 p-3 backdrop-blur" data-testid="bulk-action-bar">
        <span className="text-xs text-slate-400">
          <span className="font-mono text-slate-100">{checkedIds.length}</span> selected
          {checkedIds.length > 0 && <span className="ml-2 text-[11px] text-slate-500">· <span className="text-emerald-300">{selApprovable}</span> ready to approve · <span className="text-amber-300">{selNeedsRender}</span> need render</span>}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" data-testid="bulk-approve-btn" disabled={busy || running || !checkedIds.length} onClick={approveChecked}
            className="gap-1.5 bg-emerald-500 text-slate-950 hover:bg-emerald-400 disabled:opacity-40"><CheckCircle2 className="h-4 w-4" /> Approve checked{checkedIds.length ? ` (${selApprovable})` : ""}</Button>
          <Button size="sm" data-testid="bulk-render-btn" disabled={busy || running || !checkedIds.length} onClick={openRenderPreview}
            className="gap-1.5 bg-amber-500 text-slate-950 hover:bg-amber-400 disabled:opacity-40"><Coins className="h-4 w-4" /> Paid render checked{checkedIds.length ? ` (${selNeedsRender})` : ""}</Button>
        </div>
      </div>

      {/* cost preview dialog */}
      <Dialog open={!!preflight} onOpenChange={(o) => !o && setPreflight(null)}>
        <DialogContent className="max-w-md border-amber-500/25 bg-[#12161f] text-slate-100" data-testid="bulk-render-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-2xl"><Coins className="h-5 w-5 text-amber-400" /> Paid render — cost preview</DialogTitle>
            <DialogDescription className="text-slate-400">This makes billed ElevenLabs calls. Only passages that need it are rendered — fresh takes are never re-billed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border border-border bg-[#0d1016] px-2 py-1.5 text-center"><div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">Passages</div><div className="font-mono text-lg text-slate-100">{preflight?.eligible}</div></div>
              <div className="rounded-md border border-border bg-[#0d1016] px-2 py-1.5 text-center"><div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">Characters</div><div className="font-mono text-lg text-slate-100">{preflight?.estimated_chars?.toLocaleString()}</div></div>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2 py-1.5 text-center"><div className="font-mono text-[9px] uppercase tracking-wider text-amber-300/80">Est. credits</div><div className="font-mono text-lg text-amber-300">{preflight?.estimated_credits?.toLocaleString()}</div></div>
            </div>
            <div className="text-[11px] text-slate-500">{preflight?.billing_rate_label}</div>
            {preflight?.skipped_fresh > 0 && <div className="rounded bg-slate-700/20 px-2 py-1 text-[11px] text-slate-400">{preflight.skipped_fresh} checked passage(s) already have fresh takes and won&apos;t be re-billed.</div>}
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPreflight(null)} className="border-border text-slate-300">Cancel</Button>
            <Button data-testid="confirm-bulk-render" disabled={busy} onClick={startRender} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />} Render {preflight?.eligible} · ~{preflight?.estimated_credits} credits
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BulkReviewTable;
